import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { ApiToken, Locale } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import {
  ApiKeysApiError,
  createApiToken,
  deleteApiToken,
  fetchApiTokens,
  revokeApiToken,
} from './api-keys-api';

/**
 * Section CLÉS D'API (T8, XPHOTO-13) — console SUPERADMIN (`/superadmin/cles-api`).
 *
 * Réservée au superadmin (les routes back renvoient 403 sinon). Le superadmin crée
 * une clé (→ jeton BRUT affiché UNE SEULE FOIS avec bouton copier + avertissement
 * « ne sera plus affiché »), liste ses clés (label, dates, état révoqué), RÉVOQUE
 * une clé active (soft — reste listée « Révoquée ») puis la SUPPRIME définitivement
 * (hard delete — disparaît de la liste). Les deux actions sont en confirmation à 2
 * temps, jamais la couleur seule (icône + texte).
 *
 * Le jeton sert à appeler l'API externe `/api/v1/*` en lecture seule (Bearer),
 * scopée à la bibliothèque du propriétaire.
 */

const inputClass =
  'h-11 flex-1 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

export function ApiKeysSection(): React.JSX.Element {
  const { t, locale } = useI18n();
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Jeton brut fraîchement créé (affiché une seule fois), ou null.
  const [freshSecret, setFreshSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Id de la clé en attente de confirmation de révocation.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  // Id de la clé dont l'action (révoquer/supprimer) est en vol : garde de
  // double-soumission + `disabled` du bouton correspondant (audit F9).
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);

  /**
   * Chargement de la liste, ANNULABLE (audit F9).
   *
   * `load()` est rappelée après chaque mutation (`submit`/`revoke`/`remove`) : sans
   * annulation, révoquer deux clés rapidement laissait la réponse LENTE du premier
   * chargement écraser la liste fraîche du second. On abandonne donc systématiquement
   * le chargement précédent — et celui en vol au démontage.
   */
  const loadControllerRef = useRef<AbortController | null>(null);

  const load = useCallback(async (): Promise<void> => {
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    setLoadError(false);
    try {
      const list = await fetchApiTokens(controller.signal);
      if (controller.signal.aborted) return;
      setTokens(list);
    } catch {
      if (controller.signal.aborted) return;
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => loadControllerRef.current?.abort();
  }, [load]);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setCreateError(null);
    setFreshSecret(null);
    setCopied(false);
    const trimmed = label.trim();
    if (trimmed.length === 0) {
      setCreateError(t('settings.apiKeys.errorLabel'));
      return;
    }
    setBusy(true);
    try {
      const created = await createApiToken(trimmed);
      setFreshSecret(created.secret);
      setLabel('');
      await load();
    } catch (err) {
      if (err instanceof ApiKeysApiError && err.status === 400) {
        setCreateError(t('settings.apiKeys.errorLabel'));
      } else {
        setCreateError(t('settings.apiKeys.errorGeneric'));
      }
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async (): Promise<void> => {
    if (freshSecret === null) return;
    try {
      await navigator.clipboard.writeText(freshSecret);
      setCopied(true);
    } catch {
      // Presse-papiers indisponible : l'utilisateur peut sélectionner le champ.
    }
  };

  const revoke = async (id: string): Promise<void> => {
    if (rowBusyId !== null) return; // garde de double-soumission (patron du projet)
    setRowBusyId(id);
    try {
      await revokeApiToken(id);
      setConfirmingId(null);
      await load();
    } catch {
      // Échec : le rechargement reflète l'état réel (la clé est-elle révoquée ?).
      await load();
    } finally {
      setRowBusyId(null);
    }
  };

  const remove = async (id: string): Promise<void> => {
    if (rowBusyId !== null) return; // garde de double-soumission (patron du projet)
    setRowBusyId(id);
    try {
      await deleteApiToken(id);
      setConfirmingId(null);
      await load();
    } catch {
      // Échec : le rechargement reflète l'état réel (la clé existe-t-elle encore ?).
      await load();
    } finally {
      setRowBusyId(null);
    }
  };

  return (
    <section
      id="admin-api-keys"
      role="tabpanel"
      aria-labelledby="admin-api-keys-heading"
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-api-keys-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <KeyIcon className="h-5 w-5" aria-hidden="true" />
          {t('settings.apiKeys.title')}
        </h3>
        <p className="text-sm text-muted">{t('settings.apiKeys.intro')}</p>
      </div>

      {/* Création. */}
      <form
        onSubmit={(e) => void submit(e)}
        className="flex flex-col gap-3 rounded-lg border border-border p-4"
      >
        <label htmlFor="apikey-label" className="text-sm font-medium text-fg">
          {t('settings.apiKeys.labelLabel')}
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            id="apikey-label"
            type="text"
            value={label}
            maxLength={120}
            placeholder={t('settings.apiKeys.labelPlaceholder')}
            onChange={(e) => setLabel(e.target.value)}
            aria-invalid={createError !== null}
            className={inputClass}
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {busy ? t('settings.apiKeys.creating') : t('settings.apiKeys.create')}
          </button>
        </div>
        {createError && (
          <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {createError}
          </p>
        )}
      </form>

      {/* Jeton brut fraîchement créé (affiché une seule fois). */}
      {freshSecret !== null && (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-lg border border-warning bg-warning/10 p-4"
        >
          <p className="inline-flex items-center gap-2 text-sm font-medium text-fg">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {t('settings.apiKeys.secretWarning')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="flex-1 break-all rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg">
              {freshSecret}
            </code>
            <button
              type="button"
              onClick={() => void copySecret()}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {copied ? (
                <CheckIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ClipboardDocumentIcon className="h-5 w-5" aria-hidden="true" />
              )}
              {copied ? t('settings.apiKeys.copied') : t('settings.apiKeys.copy')}
            </button>
          </div>
        </div>
      )}

      {/* Liste des clés. */}
      {loadError ? (
        <p role="alert" className="text-sm text-danger">
          {t('settings.apiKeys.errorLoad')}
        </p>
      ) : tokens === null ? (
        <p className="text-sm text-muted">{t('settings.apiKeys.loading')}</p>
      ) : tokens.length === 0 ? (
        <p className="text-sm text-muted">{t('settings.apiKeys.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {tokens.map((token) => (
            <li
              key={token.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium text-fg">{token.label}</span>
                <span className="text-xs text-muted">
                  {token.revokedAt !== null
                    ? t('settings.apiKeys.revoked')
                    : token.lastUsedAt !== null
                      ? interpolate(t('settings.apiKeys.lastUsed'), {
                          date: formatDate(token.lastUsedAt, locale),
                        })
                      : t('settings.apiKeys.neverUsed')}
                </span>
              </div>
              {/* Clé ACTIVE → « Révoquer » (soft). Clé RÉVOQUÉE → « Supprimer »
                  (hard delete définitif). Les deux en confirmation à 2 temps, le
                  même `confirmingId` (une clé n'a jamais les deux états). */}
              {token.revokedAt === null ? (
                confirmingId === token.id ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void revoke(token.id)}
                      disabled={rowBusyId !== null}
                      className="inline-flex items-center gap-1 rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-danger-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      {t('settings.apiKeys.confirmRevoke')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingId(null)}
                      className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                    >
                      {t('settings.apiKeys.cancel')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingId(token.id)}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    {t('settings.apiKeys.revoke')}
                  </button>
                )
              ) : confirmingId === token.id ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void remove(token.id)}
                    disabled={rowBusyId !== null}
                    className="inline-flex items-center gap-1 rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-danger-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    {t('settings.apiKeys.deleteConfirm')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingId(null)}
                    className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    {t('settings.apiKeys.cancel')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingId(token.id)}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
                >
                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                  {t('settings.apiKeys.delete')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Lien vers la doc. */}
      <p className="text-sm text-muted">
        <a
          href="/api/v1/openapi.json"
          target="_blank"
          rel="noreferrer"
          className="text-brand underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {t('settings.apiKeys.docLink')}
        </a>
      </p>
    </section>
  );
}

/**
 * Formate une date ISO dans la langue de L'APPLICATION (jamais lève sur date
 * invalide). Sans `locale` explicite, `toLocaleString()` suivait la langue du
 * NAVIGATEUR : un utilisateur au navigateur français ayant choisi EN dans
 * l'application voyait quand même des dates au format français.
 */
function formatDate(iso: string, locale: Locale): string {
  try {
    return new Date(iso).toLocaleString(locale);
  } catch {
    return iso;
  }
}
